import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Video, Send, Trash2, Clock, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

const MAX_VOICE_SECS = 120;
const MAX_VIDEO_SECS = 60;
const MAX_VIDEO_MB = 25;

const videoDuration = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration); };
  v.onerror = () => resolve(0);
  v.src = URL.createObjectURL(file);
});

export const useVoiceRecorder = () => {
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [blob, setBlob] = useState(null);
  const rec = useRef(null);
  const timer = useRef(null);

  const stop = () => { if (rec.current?.state === "recording") rec.current.stop(); clearInterval(timer.current); setRecording(false); };
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = [];
      const r = new MediaRecorder(stream);
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => { setBlob(new Blob(chunks, { type: r.mimeType || "audio/webm" })); stream.getTracks().forEach((t) => t.stop()); };
      rec.current = r; r.start(); setBlob(null); setSecs(0); setRecording(true);
      timer.current = setInterval(() => setSecs((s) => { if (s + 1 >= MAX_VOICE_SECS) stop(); return s + 1; }), 1000);
    } catch {
      toast.error("Microphone access was blocked. Allow it in your browser to record a voice note.");
    }
  };
  useEffect(() => () => clearInterval(timer.current), []);
  return { recording, secs, blob, start, stop, clear: () => setBlob(null) };
};

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const MyQuestions = ({ items }) => items.length === 0 ? null : (
  <div className="mt-8 border-t border-border pt-6" data-testid="my-tutor-questions">
    <p className="font-mono text-[11px] uppercase tracking-widest text-primary">Your questions</p>
    <div className="mt-3 space-y-3">
      {items.map((q) => (
        <div key={q.id} className="rounded-xl bg-secondary p-4 text-sm" data-testid={`my-question-${q.id}`}>
          <p className="text-muted-foreground">{q.message || (q.attachment_kind === "audio" ? "Voice note" : "Video")}</p>
          {q.reply ? (
            <div className="mt-3 border-l-2 border-primary pl-3" data-testid={`my-question-reply-${q.id}`}>
              <p className="text-xs font-medium text-primary">Jason replied</p>
              {q.reply.message && <p className="mt-1 whitespace-pre-line text-foreground">{q.reply.message}</p>}
              {q.reply.attachment_kind === "audio" && <audio controls src={`${process.env.REACT_APP_BACKEND_URL}${q.reply.attachment_url}`} className="mt-2 w-full" />}
            </div>
          ) : <p className="mt-2 text-xs text-muted-foreground">Awaiting reply · within 24 hours</p>}
        </div>
      ))}
    </div>
  </div>
);

export const AskTutor = ({ instrumentId, courseId }) => {
  const [mine, setMine] = useState([]);
  const loadMine = () => api.get("/tutor-questions/mine", { params: { instrument: instrumentId, course_id: courseId } }).then(({ data }) => setMine(data.questions || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadMine().then(() => api.post("/tutor-questions/mark-seen", { instrument: instrumentId, course_id: courseId }).catch(() => {})); }, [instrumentId, courseId]);
  const [message, setMessage] = useState("");
  const [video, setVideo] = useState(null);
  const [sending, setSending] = useState(false);
  const voice = useVoiceRecorder();

  const pickVideo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_VIDEO_MB * 1024 * 1024) return toast.error(`Video must be under ${MAX_VIDEO_MB}MB`);
    const d = await videoDuration(f);
    if (d > MAX_VIDEO_SECS + 1) return toast.error(`Please keep videos to ${MAX_VIDEO_SECS} seconds or less`);
    voice.clear(); setVideo(f);
  };

  const send = async () => {
    const file = video || (voice.blob ? new File([voice.blob], "voice-note.webm", { type: voice.blob.type }) : null);
    if (!message.trim() && !file) return toast.error("Type a question, record a voice note or add a video");
    const fd = new FormData();
    fd.append("instrument", instrumentId); fd.append("course_id", courseId); fd.append("message", message.trim());
    if (file) fd.append("file", file);
    setSending(true);
    try {
      await api.post("/tutor-questions", fd);
      toast.success("Sent to Jason. You'll get a reply within 24 hours.");
      setMessage(""); setVideo(null); voice.clear(); loadMine();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send, please try again");
    } finally { setSending(false); }
  };

  return (
    <div className="rounded-3xl border border-primary/30 bg-card p-6 sm:p-8" data-testid="ask-tutor">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><MessageCircleQuestion size={14} /> Further ask your tutor</p>
      <h3 className="mt-2 font-serif text-2xl">Stuck on something? Ask Jason directly.</h3>
      <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><Clock size={14} /> Responses within 24 hours, by email.</p>
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={3000} data-testid="ask-tutor-message"
        placeholder="Type your question… e.g. 'In lesson 6, why does the F♯ still apply later in the bar?'"
        className="mt-5 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none" />
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {!voice.recording ? (
          <Button type="button" variant="outline" onClick={() => { setVideo(null); voice.start(); }} className="rounded-full border-border" data-testid="ask-tutor-record-btn">
            <Mic size={16} className="mr-2" /> Record voice note
          </Button>
        ) : (
          <Button type="button" onClick={voice.stop} className="rounded-full bg-red-600 text-white hover:bg-red-700" data-testid="ask-tutor-stop-btn">
            <Square size={14} className="mr-2" /> Stop · {fmt(voice.secs)} / 2:00
          </Button>
        )}
        <label className="inline-flex cursor-pointer items-center rounded-full border border-border px-4 py-2 text-sm transition-colors hover:border-primary" data-testid="ask-tutor-video-label">
          <Video size={16} className="mr-2" /> Add short video
          <input type="file" accept="video/*" className="hidden" onChange={pickVideo} data-testid="ask-tutor-video-input" />
        </label>
        <span className="text-xs text-muted-foreground">Voice notes up to 2 min · videos up to 60 sec ({MAX_VIDEO_MB}MB)</span>
      </div>
      {(voice.blob || video) && (
        <div className="mt-4 flex items-center gap-3 rounded-xl bg-secondary p-3" data-testid="ask-tutor-attachment">
          {voice.blob ? <audio controls src={URL.createObjectURL(voice.blob)} className="h-9 max-w-full" /> : <span className="truncate text-sm">{video.name}</span>}
          <button onClick={() => { voice.clear(); setVideo(null); }} aria-label="Remove attachment" className="ml-auto text-muted-foreground hover:text-red-500" data-testid="ask-tutor-remove-attachment"><Trash2 size={16} /></button>
        </div>
      )}
      <Button onClick={send} disabled={sending || voice.recording} className="mt-5 rounded-full bg-primary px-6 text-primary-foreground hover:bg-primary/90" data-testid="ask-tutor-send-btn">
        <Send size={15} className="mr-2" /> {sending ? "Sending…" : "Send to Jason"}
      </Button>
      <MyQuestions items={mine} />
    </div>
  );
};
